/**
 * Auto Reply node config: defaults, normalisation, validation and summaries.
 *
 * Pure TypeScript mirror of `backend/app/services/auto_reply_rule.py`. The two
 * must agree on defaults, clamps and validation wording, because the builder
 * blocks Save on these checks and the backend re-checks them on activation.
 */
import type { AutoReplyConfig, AutoReplyShared } from '@/types/workflow'

export const MAX_FOLLOW_UPS = 5

const TONES: AutoReplyShared['tone'][] = ['professional', 'friendly', 'casual', 'persuasive', 'urgent']
const PRIORITIES = ['low', 'medium', 'high'] as const
const LEAD_STATUSES = ['contacted', 'engaged', 'qualified'] as const

const DEFAULT_SHARED: AutoReplyShared = {
  write_mode: 'ai',
  tone: 'professional',
  purpose: 'follow_up',
  template_id: null,
  material_ids: [],
  contact_fields: ['job_title', 'company_name', 'company_industry'],
  custom_instructions: '',
  subject: '',
  body: '',
  threading: 'same_thread',
}

export const AUTO_REPLY_DEFAULT: AutoReplyConfig = {
  no_reply: {
    follow_up_count: 2,
    wait_value: 3,
    wait_unit: 'days',
    business_days_only: false,
    shared: DEFAULT_SHARED,
    overrides: {},
    end_behaviour: 'end_quietly',
  },
  reply: {
    stop_follow_ups: true,
    task: {
      type: 'follow_up',
      title_template: 'Follow up with {contact}',
      description_template: '',
      include_reply_snippet: true,
      priority: 'medium',
      due_in_days: 2,
    },
    team_member_emails: [],
    set_lead_status: null,
    add_tag: 'Replied',
  },
}

const intIn = (value: unknown, lo: number, hi: number, fallback: number): number => {
  const n = Math.trunc(Number(value))
  if (value === null || value === undefined || value === '' || Number.isNaN(n)) return fallback
  return Math.max(lo, Math.min(hi, n))
}

const normalizeShared = (raw?: Partial<AutoReplyShared> | null): AutoReplyShared => {
  const out: AutoReplyShared = {
    ...DEFAULT_SHARED,
    material_ids: [],
    contact_fields: [...DEFAULT_SHARED.contact_fields],
  }
  const src = (raw || {}) as Record<string, unknown>
  for (const key of Object.keys(out) as (keyof AutoReplyShared)[]) {
    if (src[key] !== null && src[key] !== undefined) (out as any)[key] = src[key]
  }
  if (out.write_mode !== 'ai' && out.write_mode !== 'manual') out.write_mode = 'ai'
  if (out.threading !== 'same_thread' && out.threading !== 'fresh') out.threading = 'same_thread'
  if (!TONES.includes(out.tone)) out.tone = 'professional'
  return out
}

/** Fully-defaulted, clamped copy of a raw config. Idempotent, never mutates the input. */
export function normalizeAutoReply(raw?: Partial<AutoReplyConfig> | null): AutoReplyConfig {
  const nr: any = raw?.no_reply || {}
  const rp: any = raw?.reply || {}
  const task: any = rp.task || {}
  const d = AUTO_REPLY_DEFAULT

  const overrides: Record<string, Partial<AutoReplyShared>> = {}
  for (const [k, v] of Object.entries(nr.overrides || {})) {
    if (v && typeof v === 'object' && Object.keys(v as object).length > 0) {
      overrides[String(k)] = { ...(v as object) }
    }
  }

  const tagRaw = 'add_tag' in rp ? rp.add_tag : d.reply.add_tag
  // Not trimmed: this runs on every keystroke, so trimming would make "Hot lead" untypeable.
  // A tag of only spaces is no tag. The backend trims when it applies the tag.
  const tag = tagRaw === null || tagRaw === undefined || String(tagRaw).trim() === '' ? null : String(tagRaw)

  return {
    no_reply: {
      follow_up_count: intIn(nr.follow_up_count, 1, MAX_FOLLOW_UPS, d.no_reply.follow_up_count),
      wait_value: intIn(nr.wait_value, 1, 365, d.no_reply.wait_value),
      wait_unit: nr.wait_unit === 'hours' ? 'hours' : 'days',
      business_days_only: Boolean(nr.business_days_only),
      shared: normalizeShared(nr.shared),
      overrides,
      end_behaviour: 'end_quietly',
    },
    reply: {
      stop_follow_ups: true,
      task: {
        type: task.type === 'custom' ? 'custom' : 'follow_up',
        title_template: task.title_template === undefined || task.title_template === null
          ? d.reply.task.title_template
          : String(task.title_template),
        description_template: String(task.description_template || ''),
        include_reply_snippet: task.include_reply_snippet === undefined ? true : Boolean(task.include_reply_snippet),
        priority: (PRIORITIES as readonly string[]).includes(task.priority) ? task.priority : 'medium',
        due_in_days: intIn(task.due_in_days, 1, 30, d.reply.task.due_in_days),
      },
      team_member_emails: (Array.isArray(rp.team_member_emails) ? rp.team_member_emails : [])
        .filter((e: unknown): e is string => typeof e === 'string' && e.trim() !== '')
        .map((e: string) => e.trim()),
      set_lead_status: (LEAD_STATUSES as readonly string[]).includes(rp.set_lead_status) ? rp.set_lead_status : null,
      add_tag: tag,
    },
  }
}

/** The shared setup with follow-up `step`'s override (1-based) applied on top. */
export function effectiveFollowUp(cfg: AutoReplyConfig, step: number): AutoReplyShared {
  const merged: AutoReplyShared = { ...cfg.no_reply.shared }
  const ov = cfg.no_reply.overrides[String(step)] || {}
  for (const [k, v] of Object.entries(ov)) {
    if (v !== undefined && v !== null) (merged as any)[k] = v
  }
  return merged
}

/** Problems that must be fixed before the workflow can be saved or activated. Empty means valid. */
export function validateAutoReply(cfg: AutoReplyConfig): string[] {
  const errs: string[] = []
  if (cfg.reply.team_member_emails.length === 0) {
    errs.push('Add at least one team member to the assignment pool.')
  }
  if (!cfg.reply.task.title_template.trim()) {
    errs.push('The task needs a title.')
  }
  let sharedReported = false
  for (let step = 1; step <= cfg.no_reply.follow_up_count; step++) {
    const s = effectiveFollowUp(cfg, step)
    if (s.write_mode !== 'manual' || s.body.trim()) continue
    if (cfg.no_reply.overrides[String(step)]) {
      errs.push(`Follow-up ${step} is in manual mode and needs an email body.`)
    } else if (!sharedReported) {
      errs.push('Follow-ups are in manual mode and need an email body.')
      sharedReported = true
    }
  }
  return errs
}

export function setOverride(cfg: AutoReplyConfig, step: number, patch: Partial<AutoReplyShared>): AutoReplyConfig {
  const key = String(step)
  return {
    ...cfg,
    no_reply: {
      ...cfg.no_reply,
      overrides: { ...cfg.no_reply.overrides, [key]: { ...(cfg.no_reply.overrides[key] || {}), ...patch } },
    },
  }
}

export function clearOverride(cfg: AutoReplyConfig, step: number): AutoReplyConfig {
  const { [String(step)]: _removed, ...rest } = cfg.no_reply.overrides
  return { ...cfg, no_reply: { ...cfg.no_reply, overrides: rest } }
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

/** One short line per branch, for the canvas card. */
export function summarizeAutoReply(cfg: AutoReplyConfig): { noReply: string; reply: string } {
  const nr = cfg.no_reply
  const unit = nr.wait_unit === 'hours' ? 'hour' : 'day'
  const pool = cfg.reply.team_member_emails.length
  return {
    noReply: `${plural(nr.follow_up_count, 'follow-up', 'follow-ups')}, ${plural(nr.wait_value, unit, `${unit}s`)} apart`,
    reply: pool === 0 ? 'No team selected' : `Task to ${plural(pool, 'teammate', 'teammates')}`,
  }
}
